import { useCallback, useMemo } from 'react';

import { Chart, LegendTilesConfig } from '@dbt-labs/charts';
import { Link, Section } from '@dbt-labs/sourdough';

import { CostInsight, Grouping } from '../../typings/costInsights';
import { getTimePeriodLabel, groupDataByTimeGrouping } from '../../util/costInsights';
import { TEAL_COLOR_WITH_OPACITY } from './const';
import { GroupingSelector } from './GroupingSelector';
import { InfoTooltip } from './InfoTooltip';
import {
  createEmptyLegendTile,
  legendTilesBuilder,
  usePartialDataTooltipHeader,
} from './utils';

const PRIMARY_DATA_LABEL = 'Total cost';
const SECONDARY_DATA_LABEL = 'Total cost without optimization';
const AREA_DATA_LABEL = 'Total cost reduction';

type CostChartProps = {
  data: CostInsight[];
  isLoading: boolean;
  isPreparing?: boolean;
  grouping: Grouping;
  setGrouping?: (grouping: Grouping) => void;
  infoTooltip?: React.ReactNode;
  connectionSettingsUrl?: string;
  enableGroupingSelector?: boolean;
  statusBadge?: React.ReactNode;
  selector?: React.ReactNode;
};

export const CostChart = ({
  data,
  isLoading,
  isPreparing = false,
  grouping,
  setGrouping,
  infoTooltip,
  connectionSettingsUrl,
  enableGroupingSelector = true,
  statusBadge,
  selector,
}: CostChartProps) => {
  const groupedData = useMemo(
    () => (data ? groupDataByTimeGrouping(data, grouping) : []),
    [data, grouping],
  );

  // Combine cost and cost saved into a single array for stackedTimeSeries
  const chartData = useMemo<{
    totalCostData: Array<{ date: Date; value: number }>;
    totalCostWithoutSavingsData: Array<{ date: Date; value: number }>;
    totalCostSavingsData: Array<{ date: Date; value: number }>;
  }>(() => {
    const totalCostData: Array<{ date: Date; value: number }> = [];
    const totalCostWithoutSavingsData: Array<{ date: Date; value: number }> = [];
    const totalCostSavingsData: Array<{ date: Date; value: number }> = [];
    groupedData.forEach((row) => {
      totalCostData.push({ date: row.date, value: row.executionCost });
      totalCostWithoutSavingsData.push({
        date: row.date,
        value: row.executionCostSaved + row.executionCost,
      });
      totalCostSavingsData.push({
        date: row.date,
        value: row.executionCostSaved,
      });
    });

    return {
      totalCostData,
      totalCostWithoutSavingsData,
      totalCostSavingsData,
    };
  }, [groupedData]);

  const onTooltipHeader = usePartialDataTooltipHeader(groupedData);

  const isEmpty = !isLoading && data.length === 0;

  const emptyStateLegendTilesBuilder = useCallback(() => {
    return [
      createEmptyLegendTile(PRIMARY_DATA_LABEL, 'var(--fgVizIndigo)'),
      createEmptyLegendTile(SECONDARY_DATA_LABEL, 'var(--fgVizTeal)'),
      createEmptyLegendTile(AREA_DATA_LABEL, TEAL_COLOR_WITH_OPACITY),
    ];
  }, []);

  const legendTilesBuilderCallback = useCallback(
    (config: LegendTilesConfig) =>
      legendTilesBuilder({
        config: config,
        value: chartData.totalCostSavingsData.reduce((sum, row) => sum + row.value, 0),
        label: AREA_DATA_LABEL,
        color: TEAL_COLOR_WITH_OPACITY,
      }),
    [chartData.totalCostSavingsData],
  );

  return (
    <Section
      header={
        <div className="flex flex-col gap-y-2">
          <div className="flex items-center justify-between gap-x-2">
            <div className="flex items-center gap-x-2">
              <span>Cost {getTimePeriodLabel(grouping)}</span>
              {infoTooltip ?? (
                <InfoTooltip connectionSettingsUrl={connectionSettingsUrl} />
              )}
              {statusBadge}
            </div>
            {selector !== undefined
              ? selector
              : enableGroupingSelector &&
                setGrouping && (
                  <GroupingSelector grouping={grouping} setGrouping={setGrouping} />
                )}
          </div>
          <span className="font-caption">
            Estimated build cost reduction due to state-aware orchestration during this
            period.{' '}
            <Link
              isInternal={false}
              to="https://docs.getdbt.com/docs/explore/cost-insights"
            >
              Learn more.
            </Link>
          </span>
        </div>
      }
    >
      <Chart
        data={chartData.totalCostData}
        secondaryData={chartData.totalCostWithoutSavingsData}
        primaryColor="var(--fgVizIndigo)"
        secondaryColor="var(--fgVizTeal)"
        fillSecondaryArea={true}
        secondaryAreaStyle={{
          opacity: 0.1,
          color: 'var(--fgVizTeal)',
        }}
        secondaryValueAccessor={(row: any) => row.value}
        isLoading={isLoading || isPreparing}
        loadingMessage={isPreparing ? 'Preparing cost insights...' : undefined}
        seriesOrder={[PRIMARY_DATA_LABEL, SECONDARY_DATA_LABEL]}
        type="timeSeriesLine"
        yLabel="Cost in USD"
        primaryDataLabel={PRIMARY_DATA_LABEL}
        secondaryDataLabel={SECONDARY_DATA_LABEL}
        chartType="line"
        fillArea={true}
        timeGrouping={grouping}
        onTooltipHeader={onTooltipHeader}
        emptyStateMessage="No cost data available to view for this period"
        legendTilesBuilder={
          isEmpty || isLoading || isPreparing
            ? emptyStateLegendTilesBuilder
            : legendTilesBuilderCallback
        }
        groupAccessor={(row: any) => {
          return row.group;
        }}
        valueAccessor={(row: any) => {
          return row.value;
        }}
      />
    </Section>
  );
};
